import type {
  BulkDeleteListsResponse,
  ContractHistory,
  LicenceActivityPage,
  LicenceDetail,
  LicenceNotes,
  ListResponse,
  PrimaryContactsResponse,
  UserResponse,
} from '@shared/schemas';

import api from '@/shared/api/api-client';

import { toCsmCandidates } from '../../_shared/domain/licence-csm';
import { clientCategoriesHttp } from '../../_shared/infrastructure/client-categories.http';
import type { CreditHistory } from '../domain/credit-ledger.model';
import type { LicenceDetailGateway } from './licence-detail.gateway';

/**
 * Real implementation over the shared api client; the single place that knows the
 * admin Licence Detail endpoints and request shapes (kept 1:1 with the original
 * component). `api.get/post` unwrap the `{ success, data }` envelope.
 */
export const httpLicenceDetailGateway: LicenceDetailGateway = {
  // The client category list the Edit licence form offers and extends (#604).
  ...clientCategoriesHttp,

  loadLicence: (licenceId) => api.get<LicenceDetail>(`/licences/${licenceId}`),

  loadUsers: (licenceId) => api.get<UserResponse[]>(`/users?licence_id=${licenceId}`),

  // `origin=all`: without it, GET /lists falls back to origin=invyo (a default
  // meant for client-facing pages, see routes/lists/index.ts) and this admin
  // tab would silently drop the licence's own client-imported lists (PI-105).
  loadLists: (licenceId) => api.get<ListResponse[]>(`/lists?licence_id=${licenceId}&origin=all`),

  deleteUser: async (userId) => {
    await api.delete(`/users/${userId}`);
  },

  // The response is the user as saved; only its `archived_at` is read, so a
  // missing field (an older server) falls back to the intent.
  setUserArchived: async ({ userId, archived }) => {
    const saved = await api.patch<Partial<UserResponse> | undefined>(`/users/${userId}/archive`, {
      archived,
    });
    const archivedAt = saved?.archived_at;
    return {
      archivedAt: archivedAt !== undefined ? archivedAt : archived ? new Date().toISOString() : null,
    };
  },

  // The CSM is always sent; the one on record included, which the server keeps
  // even if they no longer qualify (#583).
  // The contract (#750) is always sent too: an emptied field clears it.
  updateLicence: ({
    licenceId,
    name,
    client_category,
    based_in,
    csm_user_id,
    contract_start_date,
    annual_profiles_target,
  }) =>
    api.patch<LicenceDetail>(`/licences/${licenceId}`, {
      name: name.trim(),
      client_category,
      based_in,
      csm_user_id,
      contract_start_date,
      annual_profiles_target,
    }),

  // The state wanted, not a toggle: a retry after a lost response settles the
  // licence where it should be instead of flipping it back.
  setLicenceStatus: async ({ licenceId, status }) => {
    const data = await api.patch<{ licence: LicenceDetail; revoked_sessions: number }>(
      `/admin/licences/${licenceId}/status`,
      { status },
    );
    return { licence: data.licence, revokedSessions: data.revoked_sessions };
  },

  deleteLicence: async (licenceId) => {
    await api.delete(`/admin/licences/${licenceId}`);
  },

  // The whole set, never a delta: the endpoint is idempotent, so a retried save
  // lands the same contacts rather than adding one twice.
  setPrimaryContacts: async ({ licenceId, userIds }) => {
    const { primary_contacts } = await api.put<PrimaryContactsResponse>(
      `/admin/licences/${licenceId}/primary-contacts`,
      { user_ids: userIds },
    );
    return primary_contacts;
  },

  // POST, not DELETE: the api client sends no body on `delete` (#577).
  deleteLists: async (listIds) => {
    const { deleted_count } = await api.post<BulkDeleteListsResponse>('/admin/lists/bulk-delete', {
      list_ids: listIds,
    });
    return deleted_count;
  },

  // The same PATCH as the list's own edit: moving to `sent` notifies the client there too.
  loadContractHistory: (licenceId) =>
    api.get<ContractHistory>(`/admin/licences/${licenceId}/contract-history`),

  updateListStatus: ({ listId, status }) => api.patch<ListResponse>(`/lists/${listId}`, { status }),

  requestListApproval: ({ listId, administratorId }) =>
    api.post<ListResponse>(`/admin/lists/${listId}/request-approval`, {
      administrator_id: administratorId,
    }),

  duplicateList: ({ listId, name }) =>
    api.post<ListResponse>(`/admin/lists/${listId}/duplicate`, { name }),

  // Every staff account the Administrators page lists, less the deactivated ones:
  // the server refuses an archived approver anyway.
  loadApprovers: async () => {
    const admins = await api.get<UserResponse[]>('/users?role=admin');
    return admins
      .filter((u) => !u.archived_at)
      .map(({ id, first_name, last_name, email }) => ({ id, first_name, last_name, email }));
  },

  // The Administrators index, narrowed to who may be picked as CSM; the server
  // re-checks the pick (#583).
  loadCsmCandidates: async () =>
    toCsmCandidates(await api.get<UserResponse[]>('/users?role=admin')),

  // The cursor is opaque (base64url today); encoded all the same, so a change of
  // its alphabet can never corrupt the query string.
  loadActivity: ({ licenceId, cursor }) =>
    api.get<LicenceActivityPage>(
      `/admin/licences/${licenceId}/activity${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
    ),

  // Admin-only routes of their own (#605): the notes are never a field of the
  // licence, whose response also reaches the client.
  loadNotes: (licenceId) => api.get<LicenceNotes>(`/admin/licences/${licenceId}/notes`),

  saveNotes: ({ licenceId, notes }) =>
    api.put<LicenceNotes>(`/admin/licences/${licenceId}/notes`, { internal_notes: notes }),

  loadCreditHistory: (licenceId) =>
    api.get<CreditHistory>(`/admin/licences/${licenceId}/credits`),

  // A fresh `Idempotency-Key` is generated per call; the backend requires the header
  // (400 without it) so a retried submit never double-grants. Minting it here keeps
  // the saga deterministic: it is a transport concern, not a business rule.
  grantCredits: ({ licenceId, amount }) =>
    api.post<{ balance: number }>(
      `/admin/licences/${licenceId}/credits`,
      { amount },
      { 'Idempotency-Key': crypto.randomUUID() },
    ),
};
